/******************************************************************************
 * AdminAutoDiag: super-admin view listing the Auto Diag compliance records
 * ("auto-diagnostics") submitted by technicians, across every technician and
 * filterable to one. Endpoints (core):
 *   GET admin/technicians                      -> technicians for the filter / names
 * The records themselves are fetched by AutoDiagTable, which this screen shares
 * with a technician's own page.
 *
 * Compliance data (plate / mileage / driving licence / photo) is sensitive and
 * must never be logged.
 *****************************************************************************/

/* Imports */
import { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import Select_ from "react-select";
import { t } from "i18next";
/***/

/* Components */
import Loading from "../../../../components/loading/Loading";
import AutoDiagTable from "../../../../components/data/auto-diag/AutoDiagTable";
/***/

/* Services */
import { api } from "../../../../services/api/api";
/***/

/**
* AdminAutoDiag: list of realized Auto Diag submissions
*/
export default function AdminAutoDiag() {
  const [techs, setTechs] = useState<EasyTech.Technician[] | null>(null);
  const [selectedTech, setSelectedTech] = useState<string>("");

  // Technicians (for the filter picker and to resolve ids to names).
  useEffect(() => {
    api.get("admin/technicians")
      .then((ret) => setTechs(ret as EasyTech.Technician[]))
      .catch((err) => {
        console.error(err);
        setTechs([]);
      });
  }, []);

  const techNameById = useMemo(() => {
    const map = new Map<string, string>();
    (techs || []).forEach((tech) => {
      map.set(tech.id, `${tech.firstName || ""} ${tech.lastName || ""}`.trim() || tech.email || tech.id);
    });
    return map;
  }, [techs]);

  const techOptions = useMemo(() =>
    (techs || []).map((tech) => ({
      value: tech.id,
      label: `${tech.firstName || ""} ${tech.lastName || ""}`.trim() || tech.email || tech.id
    })), [techs]);

  return (
    <div className="views-padded">
      <div className="my-4">
        <div className="flex justify-between items-center actions">
          <Typography variant="h4">{t("admin:autoDiag.title")}</Typography>
        </div>
      </div>

      <Card className="border mb-6">
        <CardBody className="flex flex-col gap-3">
          <Typography variant="small" className="text-gray-600">
            {t("admin:autoDiag.notice")}
          </Typography>

          {techs === null ?
            <Loading size="h-5 w-5" />
            :
            <Select_
              isClearable
              placeholder={t("admin:autoDiag.filterByTechnician")}
              options={techOptions}
              value={techOptions.find((o) => o.value === selectedTech) || null}
              onChange={(opt) => setSelectedTech(opt ? opt.value : "")} />}
        </CardBody>
      </Card>

      <AutoDiagTable technicianId={selectedTech} technicianNames={techNameById} />
    </div>
  );
}
/***/
